// src/pages/conocimiento/components/VersionesPanel.tsx
import { useState } from 'react';
import { History, RotateCcw, Eye } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { useVersionesArticulo, useVersionArticulo, useRevertirVersion } from '@/hooks/queries/useConocimiento';

interface VersionesPanelProps {
  articuloId: string;
}

export function VersionesPanel({ articuloId }: VersionesPanelProps) {
  const { data: versiones, isLoading } = useVersionesArticulo(articuloId);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const { data: versionDetalle } = useVersionArticulo(
    articuloId,
    selectedVersion || 0
  );

  const revertirVersion = useRevertirVersion();

  const handleRevertir = async (version: number) => {
    if (confirm(`¿Revertir a la versión ${version}? Se creará una nueva versión con el contenido antiguo.`)) {
      await revertirVersion.mutateAsync({ articuloId, version });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="text-center py-4 text-sm text-gray-500">
        Cargando versiones...
      </div>
    );
  }

  if (!versiones || versiones.length === 0) {
    return (
      <div className="text-center py-4 text-sm text-gray-500">
        No hay versiones anteriores
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <History className="h-4 w-4" />
        Historial de versiones
      </div>

      <div className="space-y-2">
        {versiones.map((version, index) => (
          <div
            key={version.id}
            className={`p-3 rounded-lg border ${
              index === 0 ? 'bg-blue-50 border-blue-200' : 'bg-white'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">
                  Versión {version.version}
                  {index === 0 && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                      Actual
                    </span>
                  )}
                </span>
                <p className="text-sm text-gray-500">
                  {formatDate(version.created_at)}
                </p>
                {version.modificado_por_nombre && (
                  <p className="text-xs text-gray-400">
                    Por: {version.modificado_por_nombre}
                  </p>
                )}
                {version.motivo_cambio && (
                  <p className="text-sm text-gray-600 mt-1">
                    {version.motivo_cambio}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSelectedVersion(version.version);
                    setShowPreview(true);
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
                {index !== 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRevertir(version.version)}
                    disabled={revertirVersion.isPending}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showPreview} onOpenChange={() => setShowPreview(false)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Versión {selectedVersion}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {versionDetalle && (
              <>
                <div>
                  <h3 className="font-medium text-gray-900">{versionDetalle.titulo}</h3>
                  <p className="text-sm text-gray-500">
                    {formatDate(versionDetalle.created_at)}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm">
                    {versionDetalle.contenido}
                  </pre>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
